import { Component, HostListener, NgZone, ViewEncapsulation } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { SiteConfig } from "src/app/models/site.config.models";
import { BrandingService } from "src/app/services/branding.service";
import { HttpService } from "src/app/services/http.service";
import { GetRefereeStatusResult, RefereeBasicForm } from "./referee.models";
import { ToastEvents } from "../../elements/toast/toast.events";
import { ErrorHandlerService } from "src/app/services/error-handler.service";

@Component({
    selector: 'referee',
    templateUrl: './referee.page.html',
    styleUrls: ['./referee.page.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class RefereePageComponent {
  public branding: SiteConfig|null = null;
  public formHtml: string = '';
  private referencingFormJs: HTMLScriptElement|null = null;
  private jQueryJs: HTMLScriptElement|null = null;
  public isFormInitialised: boolean = false;
  public showForm: boolean = false;
  public showBasicForm: boolean = false;
  public status: GetRefereeStatusResult|null = null;
  public formChanged: boolean = false;
  public applicantFirstName: string = '';

  public months: number[] = Array(12).fill(0).map((_, i) => i);
  public years: number[] = Array(100).fill(0).map((_, i) => i + (new Date()).getUTCFullYear() - 99).reverse();

  public form?: UntypedFormGroup;
  public showValidation: boolean = false;

  public refereeBasicForm?: RefereeBasicForm;

  constructor(
    brandingService: BrandingService,
    private ngZone: NgZone,
    private httpService: HttpService,
    private router: Router,
    formBuilder: UntypedFormBuilder,
    private errorHandler: ErrorHandlerService
  ){
    brandingService.branding$.subscribe(b => this.branding = b);

    this.setupMainForm();

    this.httpService.getRefereeForm().subscribe({
      next: result => {
        this.formHtml = result.renderedForm!;

        this.isFormInitialised = true;
      },
      error: error => {
        errorHandler.handleError(error, 'Problem getting form','There was a problem loading the referee form');
      }
    });

    this.httpService.getRefereeStatus().subscribe({
      next: result => {
        this.status = result;
        let nameSplit = this.status.applicantName.split(" ")[0];
        this.applicantFirstName = (nameSplit) ? nameSplit : 'the candidate';
        if (this.status.referenceCheckStatus !== 'InProgress' ||
            this.status.refereeStatus !== 'ReferenceRequested' ||
            this.status.refereeIsDeleted) {
          this.router.navigateByUrl('cancelled');
        }
        if (this.status.referenceType === 'Professional') {
          this.setupBasicForm(formBuilder);
        }
      },
      error: error => {
        errorHandler.handleError(error, 'Problem getting status','There was a problem getting the referee status');
      }
    });
  }

  setupMainForm() {
    window.addEventListener('form-complete', ev => this.ngZone.run(() => this.formCompleted(ev)));
    window.addEventListener('change', ev => this.ngZone.run(() => this.formChanged = true, { once: true }));
    this.jQueryJs = document.createElement('script');
    this.jQueryJs.setAttribute('src', 'https://code.jquery.com/jquery-3.6.1.min.js');

    this.referencingFormJs = document.createElement('script');
    this.referencingFormJs.setAttribute('src', '/assets/referee.form.js');

    document.head.appendChild(this.jQueryJs);
    document.body.appendChild(this.referencingFormJs);
  }

  setupBasicForm(formBuilder: UntypedFormBuilder) {
    this.refereeBasicForm = {
      organisationName: this.status!.organisationName!,
      applicantPosition: this.status!.applicantPosition!,
      startDateMonth: this.status!.startDateMonth!,
      startDateYear: this.status!.startDateYear!,
      endDateMonth: this.status!.endDateMonth,
      endDateYear: this.status!.endDateYear,
      currentRole: this.status!.currentRole,
      salary: '',
      otherInformation: ''
    };

    this.form = formBuilder.group({
      organisationName: [this.refereeBasicForm!.organisationName, Validators.required],
      applicantPosition: [this.refereeBasicForm!.applicantPosition, Validators.required],
      startDateMonth: [this.refereeBasicForm!.startDateMonth],
      startDateYear: [this.refereeBasicForm!.startDateYear],
      endDateMonth: [this.refereeBasicForm!.endDateMonth],
      endDateYear: [this.refereeBasicForm!.endDateYear],
      currentRole: [this.refereeBasicForm!.currentRole],
      salary: [this.refereeBasicForm!.salary],
      otherInformation: [this.refereeBasicForm!.otherInformation]
    });

    this.setupEndDateRequired();
  }

  setupEndDateRequired(){
    if (this.refereeBasicForm!.currentRole){
      this.setFieldOptional('endDateMonth');
      this.setFieldOptional('endDateYear');
    } else {
      this.setFieldRequired('endDateMonth');
      this.setFieldRequired('endDateYear');
    }
  }
  setFieldOptional(fieldName: string) {
    var field = this.form!.get(fieldName)!;
    field.clearValidators();
    field.updateValueAndValidity();
  }
  setFieldRequired(fieldName: string) {
    var field = this.form!.get(fieldName)!;
    field.setValidators([Validators.required]);
    field.updateValueAndValidity();
  }

  showRefereeForm(){
    this.showForm = true;

    // since angular adds and removes elements, we need to re-add the click event listeners each time it gets re-shown
    setTimeout(() => window.dispatchEvent(new Event(
      'form-rendered',
      {
        bubbles: true
      }
    )), 100);
  }

  showBasicProfessionalForm(){
    window.scrollTo(0, 0);
    this.showBasicForm = true;
  }

  showDashboard(){
    window.scrollTo(0, 0);
    if (this.showForm || this.showBasicForm) {
      window.location.reload();
    } else {
      this.showForm = false;
      this.showBasicForm = false;
    }
  }

  formCompleted(event: Event){
    var submitButtonElement = document.getElementById("submit-button-icon");
    submitButtonElement?.classList.remove("fak", "fa-talos-arrow-right");
    submitButtonElement?.classList.add("fas", "fa-spinner-third", "fa-spin");

    var ev = event as CustomEvent;
    var formInfo = ev.detail.formInfo;

    this.httpService.saveRefereeForm(formInfo).subscribe({
      next: result => {
        this.router.navigateByUrl('thankyou', { state: { applicantName: this.status!.applicantName, refereeFirstName: this.status!.refereeFirstName}});
        submitButtonElement?.classList.remove("fas", "fa-spinner-third", "fa-spin");
        submitButtonElement?.classList.add("fak", "fa-talos-arrow-right");
      },
      error: error => {
        submitButtonElement?.classList.remove("fas", "fa-spinner-third", "fa-spin");
        submitButtonElement?.classList.add("fak", "fa-talos-arrow-right");
        this.errorHandler.handleError(error, 'Save failed', 'There was a problem saving the referee form');
      }
    });
  }

  // @HostListener allows us to also guard against browser refresh, close, etc.
  @HostListener('window:beforeunload', ['$event'])
  @HostListener('window:popstate', ['$event'])
  unloadNotification($event: any) {
    if(this.formChanged) {
      $event.returnValue = "Any changes that you made may not be saved";
      return false;
    }
    return true;
  }

  onOrganisationNameChange(event: Event){
    const value = (event.target as HTMLInputElement).value;
    this.refereeBasicForm!.organisationName = value;
    this.form!.controls['organisationName'].setValue(value);
  }

  onApplicantPositionChange(event: Event){
    const value = (event.target as HTMLInputElement).value;
    this.refereeBasicForm!.applicantPosition = value;
    this.form!.controls['applicantPosition'].setValue(value);
  }

  onCurrentRoleChange(event: Event){
    const value = Boolean((event.target as HTMLInputElement).checked);
    this.refereeBasicForm!.currentRole = value;
    this.status!.currentRole = value;
    this.form!.controls['currentRole'].setValue(value);
    this.setupEndDateRequired();
  }

  onSalaryChange(event: Event){
    const value = (event.target as HTMLInputElement).value;
    this.refereeBasicForm!.salary = value;
    this.form!.controls['salary'].setValue(value);
  }

  onOtherInformationChange(event: Event){
    const value = (event.target as HTMLInputElement).value;
    this.refereeBasicForm!.otherInformation = value;
    this.form!.controls['otherInformation'].setValue(value);
  }

  onStartDateMonthChange(event: Event){
    const value = Number((event.target as HTMLInputElement).value);
    this.refereeBasicForm!.startDateMonth = value;
    this.form!.controls['startDateMonth'].setValue(value);
  }

  onStartDateYearChange(event: Event){
    const value = Number((event.target as HTMLInputElement).value);
    this.refereeBasicForm!.startDateYear = value;
    this.form!.controls['startDateYear'].setValue(value);
  }

  onEndDateMonthChange(event: Event){
    const value = Number((event.target as HTMLInputElement).value);
    this.refereeBasicForm!.endDateMonth = value;
    this.form!.controls['endDateMonth'].setValue(value);
  }

  onEndDateYearChange(event: Event){
    const value = Number((event.target as HTMLInputElement).value);
    this.refereeBasicForm!.endDateYear = value;
    this.form!.controls['endDateYear'].setValue(value);
  }

  getField(fieldName:string) {
    return this.form!.controls[fieldName];
  }
  getFieldShowValidationMessage(fieldName:string) {
    return this.showValidation &&
      this.getField(fieldName)['invalid'];
  }

  submitBasicForm(){
    if (this.form!.status === 'INVALID') {
      this.showValidation = true;
    }
    else {
      var submitButtonElement = document.getElementById("submit-button-basic-icon");
      submitButtonElement?.classList.remove("fak", "fa-talos-arrow-right");
      submitButtonElement?.classList.add("fas", "fa-spinner-third", "fa-spin");



      this.httpService.saveBasicProfessionalForm(this.refereeBasicForm!).subscribe({
        next: result => {
          this.router.navigateByUrl('thankyou', { state: { applicantName: this.status!.applicantName, refereeFirstName: this.status!.refereeFirstName}});
          submitButtonElement?.classList.remove("fas", "fa-spinner-third", "fa-spin");
      submitButtonElement?.classList.add("fak", "fa-talos-arrow-right");
        },
        error: error => {
          submitButtonElement?.classList.remove("fas", "fa-spinner-third", "fa-spin");
          submitButtonElement?.classList.add("fak", "fa-talos-arrow-right");
          this.errorHandler.handleError(error, 'Save failed', 'There was a problem saving the form');
        }
      });
    }
  }

  monthToString(month: number|null) {
    switch(month) {
      case 0:
        return 'January';
      case 1:
        return 'February';
      case 2:
        return 'March';
      case 3:
        return 'April';
      case 4:
        return 'May';
      case 5:
        return 'June';
      case 6:
        return 'July';
      case 7:
        return 'August';
      case 8:
        return 'September';
      case 9:
        return 'October';
      case 10:
        return 'November';
      case 11:
        return 'December';
      case null:
      default:
        return 'Choose your month';
    }
  }

  getTimeOfDay():string{
    let hours = (new Date()).getHours();
    if (hours < 12){
      return 'Morning';
    } else if (hours < 18){
      return 'Afternoon';
    } else {
      return 'Evening';
    }
  }
}
